/**
 * «Guardar en la libreta» beside one field (#221): a place, a plate, or a route sheet's driver.
 *
 * `SaveToBookAction` saves a party block; this saves what one field holds, as typed, to the caller's
 * own book, as that one does. An icon button after the field, named for the field it saves, so it
 * sounds as its field does: where a label repeats — each grouped consignment's places, each
 * service's — the button is told apart as its field is, by its row or its service.
 *
 * **A value already saved.** A place is its address and a plate is itself: the server keys them on
 * what was typed, so a `409` means the book holds it already, and that is all there is to say. A
 * driver is keyed on the DNI/NIE, and the name saved with it may be out of date, so a `409` offers to
 * update the entry with the name typed here, as a party block's does.
 */

import { useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { unwrap } from '@deca/api'

import { entryInputOf, labelOf, type AddressBookEntryInput } from '../app/addressBook.ts'
import { api } from '../app/api.ts'
import { addressBookErrorMessage, existingAddressBookEntryOf } from '../app/errors.ts'
import { invalidateAddressBook } from '../app/queries.ts'
import type { BookField } from './bookField.ts'
import { Dialog } from './Dialog.tsx'
import { Button, IconButton, Notice } from './primitives.tsx'
import { useToast } from './Toast.tsx'

const textOf = (value: unknown) => (typeof value === 'string' ? value.trim() : '')

export function SaveFieldToBook({ book, accessibleName }: { book: BookField; accessibleName: string }) {
  const { t } = useTranslation()
  const toast = useToast()
  const client = useQueryClient()
  const form = useFormContext()
  const valueColumn = book.kind === 'DRIVER' ? book.columns.nif : book.column
  const nameColumn = book.kind === 'DRIVER' ? (book.columns.name ?? book.columns.nif) : book.column
  // Watched, not read on click: the button has to follow the field as it is typed into.
  const [value, name] = useWatch({ control: form.control, name: [valueColumn, nameColumn] }) as unknown[]
  const ready = textOf(value) !== '' && (book.kind !== 'DRIVER' || textOf(name) !== '')

  const [busy, setBusy] = useState(false)
  /** The driver's entry the server named, and what was being saved over it, while the user decides. */
  const [existing, setExisting] = useState<{ entryId: string; body: AddressBookEntryInput } | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)

  const bodyOf = (): AddressBookEntryInput => {
    switch (book.kind) {
      case 'PLACE':
        return entryInputOf('PLACE', { address: textOf(value) })
      case 'DRIVER':
        return entryInputOf('DRIVER', { name: textOf(name), nif: textOf(value) })
      default:
        return entryInputOf(book.kind, { plate: textOf(value) })
    }
  }

  const said = (body: AddressBookEntryInput) =>
    labelOf({ name: body.name ?? null, address: body.address ?? null, plate: body.plate ?? null })

  const save = async () => {
    const body = bodyOf()
    setBusy(true)
    try {
      await unwrap(api.POST('/api/v1/address-book/entries', { body }))
      invalidateAddressBook(client)
      toast.success(t('create.saveToBook.saved', { name: said(body) }))
    } catch (caught) {
      const entryId = existingAddressBookEntryOf(caught)
      if (entryId && book.kind === 'DRIVER') {
        setUpdateError(null)
        setExisting({ entryId, body })
      } else if (entryId) {
        toast.show(t('addressBook.alreadySaved', { name: said(body) }), 'info')
      } else {
        toast.error(addressBookErrorMessage(caught, book.kind, t))
      }
    } finally {
      setBusy(false)
    }
  }

  const update = async () => {
    if (!existing) return
    setBusy(true)
    setUpdateError(null)
    try {
      await unwrap(
        api.PUT('/api/v1/address-book/entries/{entryId}', {
          params: { path: { entryId: existing.entryId } },
          body: existing.body,
        }),
      )
      invalidateAddressBook(client)
      setExisting(null)
      toast.success(t('create.saveToBook.updated', { name: said(existing.body) }))
    } catch (caught) {
      // In the dialog, where the user is looking: the entry may have been removed meanwhile.
      setUpdateError(addressBookErrorMessage(caught, book.kind, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <IconButton icon="book" label={accessibleName} onClick={() => void save()} disabled={!ready || busy} />

      <Dialog
        open={existing !== null}
        // Only ever a driver's: a DNI/NIE, where a party block's dialog says NIF/CIF.
        title={t('create.saveToBook.driverExistsTitle')}
        subtitle={t('create.saveToBook.driverExistsBody', { nif: existing?.body.nif ?? '' })}
        onClose={() => setExisting(null)}
        busy={busy}
        footer={
          <>
            <Button onClick={() => setExisting(null)} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => void update()} disabled={busy} loading={busy}>
              {t('create.saveToBook.update')}
            </Button>
          </>
        }
      >
        <div className="stack-sm">
          <p>{t('create.saveToBook.existsQuestion')}</p>
          {updateError ? <Notice tone="danger">{updateError}</Notice> : null}
        </div>
      </Dialog>
    </>
  )
}
